// §8 / §26.2 (D-346) — who you are, at the bottom of the launcher.
//
// **One component, three skins.** The launcher itself is per-skin — house, Windows and macOS each
// draw their own palette — but the answer to "who am I signed in as, and how do I stop being" must
// not differ between them. Three copies of a sign-out button is three chances for one of them to
// stop working, and this is exactly the kind of thing nobody notices is missing on the skin they do
// not use. It lives in `host/` for the same reason `DesktopIcons` does (D-276): shared shell UI that
// every skin mounts, styled from §6.2 tokens so it still belongs wherever it lands.
//
// **A guest gets the sign-in half and no status.** §26.9 puts presence behind a session, so
// offering somebody a status picker that cannot reach the server would be a control that silently
// does nothing.
import { useState } from 'react';
import { CHOOSABLE_PRESENCE, type ChosenPresence } from '@hangar/shared';
import { useKernel, useStoreValue } from '@/kernel/react';
import { beginSignIn } from '@/kernel/server';
import { COPY } from '@/copy';

export interface ProfileMenuProps {
  /** The skin's class prefix — `house`, `windows11`, `macos`. Only for its own overrides. */
  prefix: string;
}

export function ProfileMenu({ prefix }: ProfileMenuProps) {
  const kernel = useKernel();
  const identity = kernel.identity();
  // §14 — read from the store rather than held in state: the same setting is editable from
  // Settings → Account, and two pickers holding their own copy is how they come to disagree.
  const status = useStoreValue(
    kernel.settings.store,
    (values) => values['presence.status'] as ChosenPresence,
  );
  const [open, setOpen] = useState(false);

  const signedIn = identity.kind === 'user';

  const choose = (next: ChosenPresence): void => {
    setOpen(false);
    kernel.settings.set('presence.status', next);
    // Told to the server **now** rather than at the next 30-second heartbeat: a status you picked
    // and that took half a minute to appear reads as one that did not work (D-318's lesson, one
    // surface along).
    void kernel.social.setStatus(next);
  };

  return (
    <div className={`shell-profile ${prefix}-profile`} data-testid="profile-menu">
      <div className="shell-profile-row">
        {/* Initials rather than an avatar: §9 has no image for a user, and a generated monogram
            is honest about that where a stock silhouette would not be. */}
        <span className="shell-profile-avatar" aria-hidden>
          {initialsOf(signedIn ? identity.displayName : COPY.profile.guest)}
          {signedIn ? (
            <span className="presence-dot shell-profile-badge" data-status={status} />
          ) : null}
        </span>
        <span className="shell-profile-who">
          <span className="shell-profile-name" data-testid="profile-name">
            {signedIn ? identity.displayName : COPY.profile.guest}
          </span>
          {signedIn ? (
            <button
              type="button"
              className="shell-profile-status"
              data-testid="profile-status"
              aria-haspopup="listbox"
              aria-expanded={open}
              onClick={() => setOpen((was) => !was)}
            >
              <span className="presence-dot" data-status={status} aria-hidden />
              {COPY.profile.status[status]}
            </button>
          ) : (
            <span className="shell-profile-status" data-testid="profile-guest-note">
              {COPY.profile.guestNote}
            </span>
          )}
        </span>

        {signedIn ? (
          <button
            type="button"
            className="shell-profile-action"
            data-testid="profile-sign-out"
            onClick={() => void signOut()}
          >
            {COPY.profile.signOut}
          </button>
        ) : (
          <button
            type="button"
            className="shell-profile-action"
            data-testid="profile-sign-in"
            onClick={() => beginSignIn(() => kernel.shellui.showToast(COPY.signInUnavailable))}
          >
            {COPY.profile.signIn}
          </button>
        )}
      </div>

      {open && signedIn ? (
        <ul className="shell-profile-menu" role="listbox" data-testid="profile-status-menu">
          {CHOOSABLE_PRESENCE.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === status}
                data-testid={`profile-status-${option}`}
                onClick={() => choose(option)}
              >
                <span className="presence-dot" data-status={option} aria-hidden />
                {COPY.profile.status[option]}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** `John Baleshiski` → `JB`; a single name gives one letter. Never more than two. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0) return '?';
  const first = parts[0] as string;
  const last = parts.length > 1 ? (parts[parts.length - 1] as string) : '';
  return `${[...first][0] ?? ''}${[...last][0] ?? ''}`.toUpperCase();
}

/**
 * §16 `POST /auth/logout`, then a reload.
 *
 * The reload is the point and it is the same reasoning `settings-access.ts` gives: every service in
 * the kernel was built around who this was, and re-deriving that in place would be a second boot
 * path with none of the first one's tests. A logout that cannot reach the server still ends the
 * session locally, because the cookie is the server's and the reload drops everything else.
 */
async function signOut(): Promise<void> {
  try {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } catch {
    // Nothing to do: the reload below is what actually ends the session on this device.
  }
  if (typeof window !== 'undefined') window.location.assign('/');
}
